/**
 * Feedback language. Rules: "Not yet," never shaming. No exclamation-point praise. Never "trick," "hack," or "own."
 * Error-specific cues come from Meliesa's storyboards: a tens-place miss and a ones-place miss get different help.
 * Anchor cues use the Level 3 book's words: first anchor, middle anchor, end anchor, "your number, twice."
 */
import { puzzleFor } from "./puzzle";

const TENS = ["", "", "twen", "thir", "for", "fif", "six", "seven", "eigh", "nine"] as const;

/** "teens", "forties", "hundreds" — the decade a number sits in, in words. */
export function decadeName(n: number): string {
  if (n < 10) return "ones";
  if (n < 20) return "teens";
  if (n >= 100) return "hundreds";
  return `${TENS[Math.floor(n / 10)] ?? ""}ties`;
}

/** The number said in words for a hint: "the twenties", "the teens", or the number itself when a decade name reads oddly. */
const place = (n: number): string => (n < 10 || n >= 100 ? String(n) : `the ${decadeName(n)}`);

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/**
 * The flashcard's first clue — the tens.
 * Counting forward from 27 to 36: "What comes after the twenties?"
 * Counting back we never ask "what comes before" (harder); we give the two prior and let the pattern finish itself:
 * back from 90 to 81: "Hundreds, nineties… what comes next?" (Meliesa, Oct 29.)
 */
export function tensHint(prev: number, dir: "forward" | "back" = "forward", table = 0, answer?: number): string {
  // Same decade as the number counted from: on the keypad that is the move UP a column (21 → 28 in the 7s), and the
  // tens digit repeats. (Meliesa, Oct 29.) True of every table until the 12s, so the keypad sentence is left off there.
  if (answer !== undefined && Math.floor(answer / 10) === Math.floor(prev / 10)) {
    const up = dir === "forward" && table !== 12 ? "The tens digit repeats every time we go up on the keypad. " : "The tens digit repeats here. ";
    return `${up}Stay in ${place(prev)}.`;
  }
  if (dir === "forward") return `What comes after ${place(prev)}?`;
  if (prev >= 100) return `${prev + table}, ${prev}… what comes next?`;
  const d = Math.floor(prev / 10) * 10;
  return `${cap(decadeName(d + 10))}, ${decadeName(d)}… what comes next?`;
}

/** The flashcard's second clue — the ones. */
export const ENDS_HINT = "What does it end with? It ends with the number on your keypad.";

/** Shown while the student is still typing and the digits so far are right. */
export const KEEP_GOING = `Great! Now what does it end with? It ends with the number on your keypad.`;

/**
 * Extra help after a wrong answer, keyed to which digit was wrong (Meliesa's flashcards and storyboards).
 * Tens right, ones wrong  → "You're in the thirties. What does it end with? …"
 * Tens wrong              → the tens clue: "What comes after the twenties?" (needs the number counted from, `prev`)
 * Empty string when nothing fits.
 */
export function missHint(answer: number, typed: number | string, prev?: number | null, dir: "forward" | "back" = "forward", table = 0): string {
  const v = Number(typed);
  if (!Number.isFinite(v) || answer < 10 || v < 0) return "";
  const tensOk = Math.floor(v / 10) === Math.floor(answer / 10);
  const onesOk = v % 10 === answer % 10;
  if (tensOk && !onesOk) return `You're in ${place(answer)}. ${ENDS_HINT}`;
  if (!tensOk && prev !== undefined && prev !== null) return `${tensHint(prev, dir, table, answer)}${onesOk ? "" : ` Then: ${ENDS_HINT.toLowerCase()}`}`;
  if (onesOk && !tensOk) return "It ends with the right number. Which row are you in? Count the tens from the anchor.";
  return "";
}

/** The anchor nearest to a×b, or null when b is itself an anchor (or 0). Kept for callers that only need the number. */
export function nearestAnchor(a: number, b: number): number | null {
  const p = puzzleFor(a, b);
  return p.anchorFact === null || p.anchorFact === b ? null : p.anchorFact;
}

/** Half of the end anchor, written the way the book asks it: "What is half of 9? What is half of 90?" */
const halfWords = (a: number) => `Half of ${a * 10} is ${a * 5}. The 5th one is always half of the 10th.`;

/**
 * The cue shown with a problem. Plain text with **bold** markers the UI may render.
 * Follows the book: name the anchor, then say which way to count.
 */
export function anchorCue(a: number, b: number): string {
  if (b === 0) return `Count by ${a}s from zero: the 0th is 0.`;
  if (b === 1) return `**${a} × 1 = ${a}.** The first anchor is your number.`;
  if (b === 10) return `**${a} × 10 = ${a * 10}.** The end anchor is your number with a zero.`;
  if (b === 5) return `**The middle anchor.** ${halfWords(a)} It's the middle key, too: count to it.`;
  if (b === 11 && a <= 9) return `**${a} × 11 = ${a}${a}.** Your number, twice. Always first.`;
  if (b === 12 && a <= 9) {
    const eleventh = a * 11, twelfth = a * 12;
    const crosses = Math.floor(eleventh / 10) !== Math.floor(twelfth / 10);
    return `Start at the 11th: **${a} × 11 = ${eleventh}.** Count forward ${a} more.${crosses ? ` What comes after the ${decadeName(eleventh)}?` : ""}`;
  }
  if (a === 12 && b >= 1 && b <= 10) return twelvesCue(b);
  const p = puzzleFor(a, b);
  if (p.anchorFact === null) return `Count by ${a}s from zero.`;
  const name = p.anchorName === "first" ? "the first anchor" : p.anchorName === "middle" ? "the middle anchor" : p.anchorName === "end" ? "the end anchor" : `${p.anchorValue}`;
  const half = a % 2 === 0 && a !== 2 && a !== 10 && b <= 10 ?` The code repeats: below ${a * 5} is the first time through, above it the second.` : "";
  const cap = p.caption ? ` **${p.caption}.**` : "";
  return `Count ${p.dir} from ${name}: **${a} × ${p.anchorFact} = ${p.anchorValue}.**${cap}${half}`;
}

const ordinal = (n: number) => `${n}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`;

/**
 * The division cue (Level 3, Lesson 8): the nearest anchor, then count the short way — up or down — and the answer
 * is the count. "45 is the 5th. Count up: 54 (6), 63 (7), 72 (8)." Odd tables: where the digit sits in the code.
 * Even tables: which time through — above or below the middle anchor.
 */
export function divCue(a: number, b: number): string {
  const n = a * b;
  if (a === 12 && b <= 10) return twelvesDivCue(b);
  if (b <= 3) return `**${Array.from({ length: b }, (_, i) => a * (i + 1)).join(", ")}** — count from the start. The answer is the count.`;
  const p = puzzleFor(a, b);
  if (p.anchorFact === b) return `**${n} is the ${ordinal(b)}** — ${b === 5 ? `half of ${a * 10}, the ×5 anchor` : b === 10 ? "your number with a zero, the ×10 anchor" : "the ×11 anchor: your number, twice"}. Anchors divide instantly.`;
  const an = p.anchorFact!, av = p.anchorValue!;
  const steps: string[] = [];
  if (p.dir === "forward") for (let k = an + 1; k <= b; k++) steps.push(`${a * k} (${k})`);
  else for (let k = an - 1; k >= b; k--) steps.push(`${a * k} (${k})`);
  const odd = a % 2 === 1 ? ` Where does ${n % 10} sit in the ${a}s code? The odd codes never repeat.` : a !== 12 ? ` Is ${n} above or below ${a * 5}, the middle anchor? Below is the first time through; above is the second.` : "";
  return `**${av} is the ${ordinal(an)}.** Count ${p.dir === "forward" ? "up" : "back"}: ${steps.join(", ")}.${odd}`;
}

/**
 * The 12s (Meliesa, Oct 29): "2, 4, 6, 8, SKIP A LINE." The first four begin with your number (12, 24, 36, 48);
 * skip the line (60); after it they begin with one more than your number (72, 84, 96, 108). The code says what it ends with.
 */
export function twelvesCue(b: number): string {
  const ends = [2, 4, 6, 8, 0][(b - 1) % 5]!;
  if (b <= 4) return `**12 × ${b} begins with ${b}.** The first four begin with your number. 2, 4, 6, 8, skip a line — it ends with ${ends}.`;
  if (b === 5) return `**12 × 5 = 60.** Skip a line: the middle anchor, half of 120.`;
  if (b === 10) return `**12 × 10 = 120.** The end anchor: your number with a zero.`;
  return `**12 × ${b} begins with ${b + 1}.** After the skip, they begin with one more than your number. 2, 4, 6, 8 — it ends with ${ends}.`;
}

/** Division by 12 turns that around: 84 begins with 8; for 6 to 10 the answer is one less, so 7. For 12 to 48 the answer is what it begins with. */
export function twelvesDivCue(b: number): string {
  const n = 12 * b, head = Math.floor(n / 10);
  if (b <= 4) return `**${n} begins with ${head}.** For 12 to 48, the answer is what it begins with: ${b}.`;
  if (b === 5) return `**60 is the 5th** — the skipped line, half of 120. Anchors divide instantly.`;
  if (b === 10) return `**120 is the 10th** — your number with a zero. Anchors divide instantly.`;
  if (b <= 9) return `**${n} begins with ${head}.** After the skip, the answer is one less than what it begins with: ${head} − 1 = ${b}.`;
  return "";
}

/** The classic slip: giving the number landed on instead of the count. */
export const LANDED_ON = "That's the number you landed on. The answer is how many times you counted.";

export const CORRECT = "Correct.";
export const NOT_YET = "Not yet.";
